import type { ReactNode } from 'react';
import { Svg } from '../../primitives/Svg.js';
import { useReducedMotion } from '../../../theme/hooks.js';
import type { AnimatedIconProps } from '../../primitives/types.js';

/** A page of paper with three text-lines. The lines stagger-in on mount
 *  via stroke-dashoffset (each one shorter than the last, so it reads as
 *  hand-set type filling the page). Re-keys on `active` flip → the page
 *  re-types itself, useful for highlighting feature tiles. */
export function IcnPage(props: AnimatedIconProps): ReactNode {
  const { active = true, ...rest } = props;
  const reduced = useReducedMotion();
  return (
    <Svg {...rest} iconName="page" data-on={active ? 'true' : 'false'}>
      {/* Sheet outline with a folded top-right corner. */}
      <path d="M3 2.5h7l3 3v8a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1z" />
      <path d="M10 2.5v3h3" opacity="0.65" />
      {/* Three text lines — drawn in sequence on mount. */}
      <line x1="5" y1="8" x2="11" y2="8" strokeDasharray="6" strokeDashoffset={reduced || !active ? 0 : 6}>
        {!reduced && active && (
          <animate
            key={`l1-${active ? 'on' : 'off'}`}
            attributeName="stroke-dashoffset"
            from="6"
            to="0"
            begin="0s"
            dur="0.4s"
            fill="freeze"
          />
        )}
      </line>
      <line x1="5" y1="10.4" x2="11" y2="10.4" strokeDasharray="6" strokeDashoffset={reduced || !active ? 0 : 6}>
        {!reduced && active && (
          <animate
            key={`l2-${active ? 'on' : 'off'}`}
            attributeName="stroke-dashoffset"
            from="6"
            to="0"
            begin="0.32s"
            dur="0.4s"
            fill="freeze"
          />
        )}
      </line>
      <line x1="5" y1="12.8" x2="9" y2="12.8" strokeDasharray="4" strokeDashoffset={reduced || !active ? 0 : 4}>
        {!reduced && active && (
          <animate
            key={`l3-${active ? 'on' : 'off'}`}
            attributeName="stroke-dashoffset"
            from="4"
            to="0"
            begin="0.62s"
            dur="0.4s"
            fill="freeze"
          />
        )}
      </line>
    </Svg>
  );
}
